/**
 * Tracking → progress by cluster.
 *
 * ⚠️ THE DENOMINATOR IS NOT THE FROZEN PLAN, and the caption says so. A CSV
 * baseline carries `People Team` only if whoever took the export included it, so
 * a per-cluster frozen target would be missing for some clusters and present for
 * others — and a cluster lead comparing themselves against a denominator that
 * silently changed definition between clusters is worse off than one reading a
 * live figure honestly labelled. So each cluster is measured against what it is
 * carrying right now plus what it has already won.
 *
 * The company target above it IS the frozen plan. Two different questions: "is
 * the trimester on plan" and "how is each cluster doing against its own load".
 */
import type { TrackingCluster, UnattributedRole } from '../../../types/taTracking';
import { ProgressBar, Section, clamp, pct, roleWord } from './trackingUi';

export function ClusterProgress({
  clusters,
  expectedPace,
  unresolved,
}: {
  clusters: TrackingCluster[];
  expectedPace: number;
  unresolved: UnattributedRole[];
}) {
  return (
    <Section
      title="Progress by cluster"
      aside={`Live target per cluster · marker: ${pct(expectedPace)} of the trimester has passed`}
    >
      <div className="mt-4 space-y-4">
        {clusters.map((c) => {
          const share = c.target > 0 ? c.hired / c.target : 0;
          const onPace = share >= expectedPace;
          return (
            <div key={c.key} data-testid={`cluster-${c.key}`}>
              <div className="flex items-baseline justify-between text-sm">
                <div className="font-medium text-gray-900">{c.key}</div>
                <div className="text-gray-500">
                  {c.hired} of {c.target} · {pct(share)}
                  {c.toRecruit > 0 && (
                    <span className="ml-1.5 text-gray-400">{c.toRecruit} still to recruit</span>
                  )}
                </div>
              </div>
              <ProgressBar
                pct={clamp(share)}
                markerPct={expectedPace}
                tone={onPace ? 'ok' : 'warn'}
              />
            </div>
          );
        })}
      </div>

      {unresolved.length > 0 && <Unattributed roles={unresolved} />}
    </Section>
  );
}

/**
 * Roles no cluster is accountable for.
 *
 * ⚠️ They stay in the COMPANY totals and appear in no cluster's bar — a role
 * filed under a guessed cluster moves the very figure that cluster's lead is
 * judged on. Named, because the fix is a row in Airtable (`People Team`) or a
 * line in the recruiter map, and neither happens to a role nobody can see.
 */
function Unattributed({ roles }: { roles: UnattributedRole[] }) {
  return (
    <details className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <summary className="cursor-pointer font-semibold">
        {roleWord(roles.length)} {roles.length === 1 ? 'sits' : 'sit'} in no cluster
      </summary>
      <p className="mt-1.5 text-amber-800">
        They are counted in the totals above and in none of the bars. Set <b>People Team</b> on the
        row in Airtable, or add the recruiter to the cluster map, rather than reading these bars as
        the whole picture.
      </p>
      <ul className="mt-2 space-y-1">
        {roles.map((r) => (
          <li key={`${r.positionId}-${r.title}`}>
            <b className="font-semibold">{r.title}</b>
            {r.unit && <span className="text-amber-700"> · {r.unit}</span>}
            <span className="text-amber-700"> — {r.why}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}
